// 3rd Party
import PropTypes from 'prop-types';
import React from 'react';

// Components
import {
  AbsoluteContainer,
  Div,
  FlexCol,
  FlexRow,
  H3,
  HR,
  OCBadge,
  TextSkeleton,
} from 'common/styles/oc-styled-components';
import { colors, NAV_HEIGHT } from 'common/styles/style-constants';
import { Icon } from 'opencare-crown';
import {
  getAggregateReviewData,
  reviewRatingMean,
} from 'pages/common/helpers/common-helpers';
import scrollToRef from 'utils/scrollToRef';

// Other

import { SuperPracticeBadgeSVG } from '../../../../assets/icons';
import { ClinicSubRatingBadge } from '../../../common/components/ClinicSubRatingBadge/ClinicSubRatingBadge';
import ProfileImageCircles from '../../../common/components/ProfileImageCircles/ProfileImageCircles';
import RatingLarge from '../../../common/components/RatingLarge/RatingLarge';
import {
  SuperPracticeBadge,
  SuperPracticeBadgeContainer,
} from '../../../results/components/ResultCard/styled-components';
import QuoteCloseSvg from '../svgs/QuoteCloseSvg';
import QuoteOpenSvg from '../svgs/QuoteOpenSvg';

import {
  AddressLine,
  InlineBlock,
  MostHelpfulReview,
  MostHelpfulReviewText,
  ProfileName,
} from './styled-components';

// Component
// TODO change globe icon?
const ProfileHeaderDesktop = ({
  clinicId,
  hasInsurance,
  images,
  inNetwork,
  mostHelpfulReview,
  name,
  address,
  refToReviews,
  reviews,
  reviewsCount,
  isSuperPractice,
  isProspectLead,
  aggregateSubRatings,
}) => {
  const aggregateReviews = getAggregateReviewData({
    clinicId,
    opencareReviews: reviews,
  });

  return (
    <InlineBlock
      margin={mostHelpfulReview ? '0 20px 0 0' : '0 20px 50px 0'}
      width="100%"
    >
      <FlexRow margin="40px 0 0 0">
        {isSuperPractice
          && (
            <SuperPracticeBadgeContainer size="large">
              <SuperPracticeBadge size="large">
                <SuperPracticeBadgeSVG color="#F37F6E" />
              </SuperPracticeBadge>
              <span>Super Practice</span>
            </SuperPracticeBadgeContainer>
          )}
        <FlexRow
          margin="0 0 0 auto"
          align="center"
          flexWrap="wrap"
          style={{ gap: '8px', rowGap: '8px' }}
        >
          {hasInsurance
            && (inNetwork
              ? (
                <OCBadge color={colors.sageDark} width="130px">
                  <Icon type="login" />
                  In-Network
                </OCBadge>
              )

              : (
                <>
                  {!isProspectLead
                    && (
                      <OCBadge color={colors.darkBlue} width="130px">
                        <Icon type="logout" />
                        Out-of-network
                      </OCBadge>
                    )}
                </>
              )

            )}
        </FlexRow>
      </FlexRow>
      <FlexRow>
        <FlexCol width="60%">
          <TextSkeleton
            active
            loading={!name}
            margin="5px 0 2px 0"
            paragraph={false}
            title
          >
            <ProfileName margin="10px 0 5px 0">{name}</ProfileName>
          </TextSkeleton>
          <TextSkeleton
            active
            loading={!address}
            margin="5px 0 2px 0"
            paragraph={false}
            title
          >
            <AddressLine margin="0 0 0 0">
              {address.street_number} {address.route}
              {address.suite ? `, ${address.suite}` : ''}
            </AddressLine>
            <AddressLine margin="0 0 20px 0">
              {address.locality}, {address.administrative_area_level_1_short}{' '}
              {address.postal_code}
            </AddressLine>
          </TextSkeleton>
        </FlexCol>
        <FlexRow margin="20px 0 0 auto">
          <ProfileImageCircles
            border={`3px solid ${colors.white}`}
            boxShadow="0 1px 2px rgba(0,0,0,.15)"
            height="100px"
            images={images}
            marginRight="-35px"
            width="100px"
          />
        </FlexRow>
      </FlexRow>
      {mostHelpfulReview
        && (
          <>
            <HR border={`0.5px solid ${colors.nickel}`} margin="20px 0 20px 0" />
            <FlexRow margin="0 15px 40px" align="center">
              <Div
                cursor="pointer"
                margin="0 10px"
                onClick={() => scrollToRef(refToReviews, NAV_HEIGHT + 10)}
              >
                <div id="optimizely-ar-rating-block-original">
                  <RatingLarge
                    averageRating={reviewRatingMean(reviews)}
                    reviewsCount={reviewsCount}
                    textAlign="center"
                  />
                </div>
                <Div
                  display="none"
                  width="max-content"
                  id="optimizely-ar-rating-block-experiment"
                >
                  <RatingLarge
                    sourcesCount={aggregateReviews.sources}
                    averageRating={aggregateReviews.rating}
                    reviewsCount={aggregateReviews.count}
                    textAlign="center"
                  />
                </Div>
                <FlexRow margin="10px 0 0">
                  <ClinicSubRatingBadge
                    staffRating={aggregateSubRatings?.staff?.score}
                    careQualityRating={aggregateSubRatings?.careQuality?.score}
                    equipmentRating={aggregateSubRatings?.equipment?.score}
                    height="30.85px"
                  />
                </FlexRow>
              </Div>
              <MostHelpfulReview
                padding="0 40px"
                margin="0 0 0 20px"
                as="article"
              >
                <AbsoluteContainer left="0" top="0">
                  <QuoteOpenSvg height="23" width="28" />
                </AbsoluteContainer>
                <H3 color={colors.blue} fontSize="14px">
                  MOST HELPFUL REVIEW
                </H3>
                <MostHelpfulReviewText>
                  {mostHelpfulReview.description}
                </MostHelpfulReviewText>
                <AbsoluteContainer right="10px">
                  <QuoteCloseSvg height="23" width="28" />
                </AbsoluteContainer>
              </MostHelpfulReview>
            </FlexRow>
          </>
        )}
    </InlineBlock>
  );
};

ProfileHeaderDesktop.propTypes = {
  clinicId: PropTypes.number.isRequired,
  hasInsurance: PropTypes.bool,
  images: PropTypes.array,
  inNetwork: PropTypes.bool,
  mostHelpfulReview: PropTypes.object,
  name: PropTypes.string,
  address: PropTypes.object,
  refToReviews: PropTypes.object,
  reviews: PropTypes.array,
  reviewsCount: PropTypes.number,
  isSuperPractice: PropTypes.bool,
  isProspectLead: PropTypes.bool,
  aggregateSubRatings: PropTypes.object,
};

export default ProfileHeaderDesktop;
